// Client WebSocket du mode en ligne (lot 4b) : ouvre la socket authentifiée /ws (le
// cookie de session part automatiquement, même origine), décode les messages serveur et
// expose un petit contrat { send, close }. AUCUNE logique de lobby ni de partie ici : ce
// module ne fait que le TRANSPORT ; client/online-ui.ts (lobby) et, au lot 9, la partie
// en ligne portent l'état.
//
// Le protocole (formes ClientMsg / ServerMsg) vit dans shared/net-types.ts, partagé
// verbatim avec le serveur. La socket est REFUSÉE au handshake si l'origine ou la session
// ne conviennent pas (server/ws.ts) : le navigateur ne voit alors qu'un close, sans
// détail (rien à apprendre). onClose est donc l'unique signal de fin ou d'échec.
import type { ClientMsg, ServerMsg } from '../shared/net-types'
import { decodeServerMsg } from '../shared/net-decode'

/** Contrat consommé par online-ui.ts (et, au lot 9, par la partie en ligne). */
export interface Net {
  send(msg: ClientMsg): void
  close(): void
}

/**
 * Ouvre la socket /ws et branche les rappels. `onMsg` reçoit chaque message serveur
 * décodé (JSON illisible ignoré en silence, symétrique du serveur) ; `onClose` est appelé
 * AU PLUS une fois quand la socket se ferme sans que ce soit nous (perte de connexion,
 * refus au handshake). Un close DÉLIBÉRÉ via close() ne le déclenche pas : l'appelant qui
 * part sait déjà qu'il part (sinon il afficherait « connexion perdue » à chaque retour).
 */
export function connect(onMsg: (msg: ServerMsg) => void, onClose: () => void): Net {
  // wss en HTTPS, ws en clair ; même hôte, chemin /ws (le serveur n'accepte que celui-là).
  const url = (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws'
  const ws = new WebSocket(url)
  // Vrai dès qu'on ferme volontairement OU qu'onClose a déjà été émis : garde d'idempotence.
  let done = false

  ws.addEventListener('message', (ev) => {
    let raw: unknown
    try {
      raw = JSON.parse(typeof ev.data === 'string' ? ev.data : String(ev.data))
    } catch {
      return // JSON illisible : ignoré
    }
    // Garde de forme (au lieu d'un cast aveugle) : on ne transmet à l'UI qu'un message dont le
    // `type` est un variant ServerMsg connu. Un message étranger/corrompu est ignoré en silence
    // (symétrique du JSON illisible) plutôt que passé tel quel — l'UI ne lira jamais un variant
    // fantôme. Le serveur reste la source des champs internes (non re-validés ici).
    const msg = decodeServerMsg(raw)
    if (msg) onMsg(msg)
  })

  // Un échec de connexion émet 'error' PUIS 'close' ; une perte en cours de route émet
  // 'close'. La garde `done` fait qu'onClose ne part qu'une fois, quel que soit l'ordre.
  const fireClose = (): void => {
    if (done) return
    done = true
    onClose()
  }
  ws.addEventListener('close', fireClose)
  ws.addEventListener('error', fireClose)

  return {
    send(msg: ClientMsg): void {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg))
    },
    close(): void {
      done = true // départ voulu : le close à venir ne rappellera pas onClose
      ws.close()
    },
  }
}
